import { useMemo } from "react";
import CollapsibleBlock from "../CollapsibleBlock/CollapsibleBlock";
import useCurrentAccount from "src/hooks/useCurrentAccount";
import { formatCurrency } from "src/helpers/utils";
import useRecurringTransactions from "src/hooks/useRecurringTransactions";
import useCollapsibles from "src/hooks/useCollapsibles";
import useMobile from "src/hooks/useMobile";
import RecurringTransactionsTable from "./RecurringTransactionsTable";
import useMainFilter from "src/hooks/useMainFilter";
import Cards from "../Cards/Cards";

export enum ERecurringTransactionType {
  UPCOMING = "UPCOMING",
  OVERDUE = "OVERDUE",
  SCHEDULED = "SCHEDULED",
  TODAY = "TODAY",
  ALL = "ALL",
}

interface IRecurringTransactionsCollapsible {
  title: string;
  type: ERecurringTransactionType;
  onSelectionChange?: (rows: Array<any>) => void;
  onlyCurrentAccount?: boolean;
  showSettingsButton?: boolean;
  columns?: number;
}

const RecurringTransactionsCollapsible: React.FC<
  IRecurringTransactionsCollapsible
> = ({
  type,
  title,
  onSelectionChange = () => {},
  onlyCurrentAccount = true,
  showSettingsButton = true,
  columns = 1,
}) => {
  const { isMobile } = useMobile();
  const { mainFilter } = useMainFilter();
  const { currencySymbol } = useCurrentAccount();
  const {
    updateCollapsible,
    collapsiblePreferences,
    isRecurringTransactionsMobileCollapsibleOpen,
    toggleRecurringTransactionsMobileCollapsible,
  } = useCollapsibles();
  const {
    upcomingPayments,
    recentOverduePayments,
    scheduledPayments,
    todayPayments,
    allRecurringPayments,
  } = useRecurringTransactions(onlyCurrentAccount);

  const transactions = useMemo(() => {
    if (type === ERecurringTransactionType.OVERDUE) {
      return recentOverduePayments;
    } else if (type === ERecurringTransactionType.UPCOMING) {
      return upcomingPayments;
    } else if (type === ERecurringTransactionType.SCHEDULED) {
      return scheduledPayments;
    } else if (type === ERecurringTransactionType.TODAY) {
      return todayPayments;
    } else {
      return allRecurringPayments;
    }

    return [];
  }, [
    type,
    upcomingPayments,
    recentOverduePayments,
    scheduledPayments,
    todayPayments,
    allRecurringPayments,
    mainFilter?.account,
  ]);

  const total = useMemo(() => {
    return transactions
      ?.filter?.((transaction: any) => transaction?.recurring?.enabled)
      ?.reduce?.((acc: number, curr: any) => {
        return acc + Number(curr?.total);
      }, 0);
  }, [transactions]);

  const isCollapsibleOpen = useMemo(() => {
    const {
      recurringTransactionsOverdueOpen,
      recurringTransactionsDueSoonOpen,
      recurringTransactionsTodayOpen,
      recurringTransactionsAllOpen,
    } = collapsiblePreferences;

    if (type === ERecurringTransactionType.OVERDUE) {
      return recurringTransactionsOverdueOpen;
    } else if (type === ERecurringTransactionType.UPCOMING) {
      return recurringTransactionsDueSoonOpen;
    } else if (type === ERecurringTransactionType.TODAY) {
      return recurringTransactionsTodayOpen;
    } else if (type === ERecurringTransactionType.ALL) {
      return recurringTransactionsAllOpen;
    }
  }, [
    collapsiblePreferences?.recurringTransactionsDueSoonOpen,
    collapsiblePreferences?.recurringTransactionsOverdueOpen,
    collapsiblePreferences?.recurringTransactionsTodayOpen,
    collapsiblePreferences?.recurringTransactionsAllOpen,
  ]);

  const onCollapsibleChange = (isOpen: boolean) => {
    if (type === ERecurringTransactionType.OVERDUE) {
      updateCollapsible({ recurringTransactionsOverdueOpen: isOpen });
    } else if (type === ERecurringTransactionType.UPCOMING) {
      updateCollapsible({ recurringTransactionsDueSoonOpen: isOpen });
    } else if (type === ERecurringTransactionType.TODAY) {
      updateCollapsible({ recurringTransactionsTodayOpen: isOpen });
    } else if (type === ERecurringTransactionType.ALL) {
      updateCollapsible({ recurringTransactionsAllOpen: isOpen });
    }
  };

  if (!transactions?.length && type !== ERecurringTransactionType.ALL)
    return null;

  return (
    <CollapsibleBlock
      title={title}
      badge={`${formatCurrency({
        value: total,
        currency: currencySymbol,
      })}`}
      uppercase
      open={isCollapsibleOpen}
      onChange={(isOpen: boolean) => onCollapsibleChange(isOpen)}
    >
      {type === ERecurringTransactionType.ALL ? (
        <RecurringTransactionsTable
          transactions={transactions}
          onSelectionChange={onSelectionChange}
        />
      ) : (
        <Cards columns={columns}>
          {transactions?.map?.((transaction: any, key: number) => (
            <Cards.RecurringTransaction
              key={key}
              transaction={transaction}
              type={type}
              index={key}
              showSettingsButton={showSettingsButton}
            />
          ))}
        </Cards>
      )}
    </CollapsibleBlock>
  );
};

export default RecurringTransactionsCollapsible;
